import {useEffect} from 'react';
import {useSelector} from 'react-redux';

import {appendQueryStringTemplate} from '@/hooks/externalLink/externalLinkQueryUtils';
import useSsoLinkSingle from '@/hooks/sso/useSsoLinkSingle';
import externalLinkService from '@/services/app/externalLinkService';
import iccMarketService from '@/services/icc/iccMarketService';
import iccSsoService from '@/services/icc/iccSsoService';
import userBrowserService from '@/services/user/userBrowserService';
import {makeSelectExternalLink} from '@/state/selectors/consumer';
import {selectLanguageCode} from '@/state/selectors/global';

type UseExternalLinkParams = {
    url?: string;
    externalLinkCode?: string;
    ssoTemplate?: string;
    openInBluefy?: boolean;
    ssoParams?: Record<string, unknown>;
    linkProcessor?: (url: string) => string;
    disabled?: boolean;
    deviceType?: string;
    deviceSerialNumber?: string;
    queryStringTemplate?: string;
};

/**
 * Resolves ICC AEM external link by code and/or SSO template, then opens via externalLinkService.
 * SSO single-token behavior is delegated to useSsoLinkSingle.
 */
const useExternalLink = ({
    url,
    externalLinkCode,
    ssoTemplate,
    openInBluefy,
    ssoParams,
    linkProcessor,
    disabled,
    deviceType,
    deviceSerialNumber,
    queryStringTemplate,
}: UseExternalLinkParams) => {
    const languageCode = useSelector(selectLanguageCode);
    const formattedSsoTemplate = iccSsoService.getFormattedSsoTemplate({ssoTemplate, params: ssoParams});
    const isIos = userBrowserService.isIosDevice();

    const ssoDisabled = Boolean(disabled || !ssoTemplate);
    const sso = useSsoLinkSingle({
        ssoTemplate,
        ssoParams,
        openInBluefy,
        linkProcessor,
        disabled: ssoDisabled,
        queryStringTemplate,
    });

    const {value: externalLink = url} = useSelector(makeSelectExternalLink(externalLinkCode, languageCode));
    const externalLinkWithQuery = appendQueryStringTemplate(externalLink, queryStringTemplate);

    useExternalLinkFetch(externalLinkWithQuery, externalLinkCode, disabled);

    const redirect = () => {
        if (sso.preferSsoLinkInNewTab && sso.link) {
            externalLinkService.openResolvedUrl({
                url: sso.link,
                openInBluefy: false,
                ssoTemplate,
                externalLink: externalLinkWithQuery,
                ssoParams,
                linkProcessor,
                deviceType,
                deviceSerialNumber,
                ssoLinkKey: formattedSsoTemplate ?? undefined,
            });
            return;
        }
        externalLinkService.openExternalLink({
            ssoTemplate,
            externalLink: externalLinkWithQuery,
            externalLinkCode,
            openInBluefy: openInBluefy && isIos,
            linkProcessor,
            ssoParams,
            link: sso.link,
            deviceType,
            deviceSerialNumber,
            ssoLinkKey: formattedSsoTemplate ?? undefined,
        });
    };

    return {
        redirect,
        link: sso.link,
        externalLink: externalLinkWithQuery,
    };
};

const useExternalLinkFetch = (
    externalLink: string | undefined,
    externalLinkCode: string | undefined,
    disabled: boolean | undefined
) => {
    useEffect(() => {
        if (disabled) return;
        if (externalLink) return;
        if (!externalLinkCode) return;

        iccMarketService.getExternalLink(externalLinkCode);
    }, [externalLink, externalLinkCode, disabled]);
};

export default useExternalLink;
